import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Checkbox,
  Collapse,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Folder as FolderIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { getFacilityGroups } from '../../services/facilityGroupService';
import { getAllFacilities } from '../../services/facilityService';

/**
 * FacilityAssignmentTree - Reusable component for selecting facilities via facility group hierarchy
 * 
 * Props:
 * - selectedFacilities: string[] - Array of selected facility IDs
 * - onChange: (facilityIds: string[]) => void - Callback when selection changes
 * - disabled: boolean - Disable all interactions
 * - showSummary: boolean - Show selection summary at top
 * - maxHeight: number - Max height for scrollable area (default 400)
 * - disabledFacilities: { facilityId: string, reason: string }[] - Facilities to show as unavailable
 */
const FacilityAssignmentTree = ({
  selectedFacilities = [],
  onChange,
  disabled = false,
  showSummary = true,
  maxHeight = 400,
  disabledFacilities = []
}) => {
  const [groups, setGroups] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch facility groups and facilities on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [groupsData, facilitiesData] = await Promise.all([
          getFacilityGroups(),
          getAllFacilities()
        ]);

        setGroups(groupsData || []);
        setFacilities(facilitiesData || []);

        // Expand all groups by default
        const expanded = {};
        (groupsData || []).forEach(group => {
          expanded[group.id] = true;
        });
        expanded['ungrouped'] = true;
        setExpandedGroups(expanded);
      } catch (err) {
        console.error('Error fetching facility data:', err);
        setError('Failed to load facilities');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get facilities for a specific group
  const getFacilitiesForGroup = useCallback((groupId) => {
    if (groupId === 'ungrouped') {
      return facilities.filter(facility =>
        !groups.some(group => group.facilities?.includes(facility.id))
      );
    }
    const group = groups.find(g => g.id === groupId);
    if (!group) return [];
    return facilities.filter(facility =>
      group.facilities?.includes(facility.id)
    );
  }, [groups, facilities]);

  // Filter facilities by search term
  const facilityMatchesSearch = useCallback((facility) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      facility.name?.toLowerCase().includes(term) ||
      facility.location?.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  // Check if group matches search (group name or any facility in it)
  const groupMatchesSearch = useCallback((group, groupFacilities) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      group.name?.toLowerCase().includes(term) ||
      groupFacilities.some(facilityMatchesSearch)
    );
  }, [searchTerm, facilityMatchesSearch]);

  // Toggle group expansion
  const handleToggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Create a map of disabled facility IDs to their reasons for quick lookup
  const disabledFacilityMap = useMemo(() => {
    const map = {};
    disabledFacilities.forEach(df => {
      map[df.facilityId] = df.reason || 'Already assigned';
    });
    return map;
  }, [disabledFacilities]);

  // Check if a facility is disabled
  const isFacilityDisabled = useCallback((facilityId) => {
    return !!disabledFacilityMap[facilityId];
  }, [disabledFacilityMap]);

  // Get reason why a facility is disabled
  const getDisabledReason = useCallback((facilityId) => {
    return disabledFacilityMap[facilityId];
  }, [disabledFacilityMap]);

  // Toggle individual facility selection
  const handleToggleFacility = (facilityId) => {
    if (disabled || isFacilityDisabled(facilityId)) return;
    
    const newSelection = selectedFacilities.includes(facilityId)
      ? selectedFacilities.filter(id => id !== facilityId)
      : [...selectedFacilities, facilityId];
    
    onChange(newSelection);
  };

  // Toggle all facilities in a group (excluding disabled facilities)
  const handleToggleGroup_Selection = (groupId) => {
    if (disabled) return;

    const groupFacilities = getFacilitiesForGroup(groupId);
    // Filter out disabled facilities
    const selectableFacilityIds = groupFacilities
      .filter(f => !isFacilityDisabled(f.id))
      .map(f => f.id);
    
    if (selectableFacilityIds.length === 0) return; // Nothing to select
    
    // Check if all selectable are selected
    const allSelected = selectableFacilityIds.every(id => selectedFacilities.includes(id));
    
    let newSelection;
    if (allSelected) {
      // Deselect all selectable in this group
      newSelection = selectedFacilities.filter(id => !selectableFacilityIds.includes(id));
    } else {
      // Select all selectable in this group
      const toAdd = selectableFacilityIds.filter(id => !selectedFacilities.includes(id));
      newSelection = [...selectedFacilities, ...toAdd];
    }
    
    onChange(newSelection);
  };

  // Get group checkbox state (checked, unchecked, or indeterminate)
  const getGroupCheckState = useCallback((groupId) => {
    const groupFacilities = getFacilitiesForGroup(groupId);
    if (groupFacilities.length === 0) return 'unchecked';
    
    const groupFacilityIds = groupFacilities.map(f => f.id);
    const selectedCount = groupFacilityIds.filter(id => selectedFacilities.includes(id)).length;
    
    if (selectedCount === 0) return 'unchecked';
    if (selectedCount === groupFacilityIds.length) return 'checked';
    return 'indeterminate';
  }, [getFacilitiesForGroup, selectedFacilities]);

  // Summary data
  const summaryData = useMemo(() => {
    const totalFacilities = facilities.length;
    const selectedCount = selectedFacilities.length;
    
    // Count selected per group
    const groupCounts = groups.map(group => {
      const groupFacilities = getFacilitiesForGroup(group.id);
      const selectedInGroup = groupFacilities.filter(f => selectedFacilities.includes(f.id)).length;
      return { name: group.name, selected: selectedInGroup, total: groupFacilities.length };
    });
    
    return { totalFacilities, selectedCount, groupCounts };
  }, [facilities, selectedFacilities, groups, getFacilitiesForGroup]);

  // Render group checkbox icon based on state
  const renderGroupCheckbox = (groupId) => {
    const state = getGroupCheckState(groupId);
    const iconProps = { 
      sx: { 
        color: disabled ? 'action.disabled' : (state === 'unchecked' ? 'action.active' : 'primary.main'),
        cursor: disabled ? 'default' : 'pointer'
      } 
    };
    
    if (state === 'checked') return <CheckBoxIcon {...iconProps} />;
    if (state === 'indeterminate') return <IndeterminateCheckBoxIcon {...iconProps} />;
    return <CheckBoxOutlineBlankIcon {...iconProps} />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const ungroupedFacilities = getFacilitiesForGroup('ungrouped');

  return (
    <Box>
      {/* Summary */}
      {showSummary && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip 
            label={`${summaryData.selectedCount} of ${summaryData.totalFacilities} facilities selected`}
            color={summaryData.selectedCount > 0 ? 'primary' : 'default'}
            variant="outlined"
          />
          {summaryData.selectedCount > 0 && summaryData.groupCounts
            .filter(g => g.selected > 0)
            .slice(0, 3)
            .map(g => (
              <Chip 
                key={g.name}
                label={`${g.name}: ${g.selected}/${g.total}`}
                size="small"
                variant="outlined"
              />
            ))
          }
          {summaryData.groupCounts.filter(g => g.selected > 0).length > 3 && (
            <Chip 
              label={`+${summaryData.groupCounts.filter(g => g.selected > 0).length - 3} more`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      )}

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search facilities..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          )
        }}
        sx={{ mb: 2 }}
        disabled={disabled}
      />

      {/* Tree */}
      <Paper 
        variant="outlined" 
        sx={{ 
          maxHeight, 
          overflow: 'auto',
          bgcolor: disabled ? 'action.disabledBackground' : 'background.paper'
        }}
      >
        {/* Facility Groups */}
        {groups
          .filter(group => {
            const groupFacilities = getFacilitiesForGroup(group.id);
            return groupMatchesSearch(group, groupFacilities);
          })
          .map((group) => {
            const groupFacilities = getFacilitiesForGroup(group.id);
            const filteredFacilities = groupFacilities.filter(facilityMatchesSearch);
            
            if (filteredFacilities.length === 0 && searchTerm.trim()) return null;

            return (
              <Box key={group.id}>
                {/* Group Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1,
                    px: 2,
                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: disabled ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.12)'
                    }
                  }}
                  onClick={() => handleToggleGroup(group.id)}
                >
                  <IconButton 
                    size="small" 
                    sx={{ mr: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleGroup(group.id);
                    }}
                  >
                    {expandedGroups[group.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                  
                  <Box 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleGroup_Selection(group.id);
                    }}
                    sx={{ display: 'flex', alignItems: 'center', mr: 1 }}
                  >
                    {renderGroupCheckbox(group.id)}
                  </Box>
                  
                  <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                    {group.name}
                  </Typography>
                  <Chip 
                    label={`${groupFacilities.filter(f => selectedFacilities.includes(f.id)).length}/${groupFacilities.length}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                {/* Group Facilities */}
                <Collapse in={expandedGroups[group.id]} timeout="auto" unmountOnExit>
                  <Box sx={{ pl: 4 }}>
                    {filteredFacilities.length === 0 ? (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ py: 1, px: 2, fontStyle: 'italic' }}
                      >
                        No facilities in this group
                      </Typography>
                    ) : (
                      filteredFacilities.map((facility) => {
                        const isThisFacilityDisabled = isFacilityDisabled(facility.id);
                        const disabledReason = getDisabledReason(facility.id);
                        
                        const facilityRow = (
                          <Box
                            key={facility.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              py: 0.75,
                              px: 2,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              cursor: (disabled || isThisFacilityDisabled) ? 'default' : 'pointer',
                              bgcolor: isThisFacilityDisabled ? 'action.disabledBackground' : 'transparent',
                              opacity: isThisFacilityDisabled ? 0.6 : 1,
                              '&:hover': {
                                bgcolor: (disabled || isThisFacilityDisabled) ? 'action.disabledBackground' : 'action.hover'
                              }
                            }}
                            onClick={() => handleToggleFacility(facility.id)}
                          >
                            <Checkbox
                              checked={selectedFacilities.includes(facility.id)}
                              onChange={() => handleToggleFacility(facility.id)}
                              disabled={disabled || isThisFacilityDisabled}
                              size="small"
                              sx={{ mr: 1 }}
                            />
                            {isThisFacilityDisabled ? (
                              <BlockIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                            ) : (
                              <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                            )}
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2" sx={{ color: isThisFacilityDisabled ? 'text.disabled' : 'text.primary' }}>
                                {facility.name}
                              </Typography>
                              {facility.location && (
                                <Typography variant="caption" color="text.secondary">
                                  {facility.location}
                                </Typography>
                              )}
                            </Box>
                            {isThisFacilityDisabled && (
                              <Chip 
                                label="Assigned" 
                                size="small" 
                                color="warning" 
                                variant="outlined"
                                sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                          </Box>
                        );
                        
                        return isThisFacilityDisabled ? (
                          <Tooltip 
                            key={facility.id}
                            title={disabledReason} 
                            placement="left"
                            arrow
                          >
                            {facilityRow}
                          </Tooltip>
                        ) : facilityRow;
                      })
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}

        {/* Ungrouped Facilities */}
        {ungroupedFacilities.filter(facilityMatchesSearch).length > 0 && (
          <Box>
            {/* Ungrouped Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 1,
                px: 2,
                bgcolor: 'rgba(0, 0, 0, 0.04)',
                borderBottom: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: disabled ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.08)'
                }
              }}
              onClick={() => handleToggleGroup('ungrouped')}
            >
              <IconButton 
                size="small" 
                sx={{ mr: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleGroup('ungrouped');
                }}
              >
                {expandedGroups['ungrouped'] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              
              <Box 
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleGroup_Selection('ungrouped');
                }}
                sx={{ display: 'flex', alignItems: 'center', mr: 1 }}
              >
                {renderGroupCheckbox('ungrouped')}
              </Box>
              
              <FolderIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1, color: 'text.secondary' }}>
                Ungrouped Facilities
              </Typography>
              <Chip 
                label={`${ungroupedFacilities.filter(f => selectedFacilities.includes(f.id)).length}/${ungroupedFacilities.length}`}
                size="small"
                variant="outlined"
              />
            </Box>

            {/* Ungrouped Facilities List */}
            <Collapse in={expandedGroups['ungrouped']} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 4 }}>
                {ungroupedFacilities.filter(facilityMatchesSearch).map((facility) => {
                  const isThisFacilityDisabled = isFacilityDisabled(facility.id);
                  const disabledReason = getDisabledReason(facility.id);
                  
                  const facilityRow = (
                    <Box
                      key={facility.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 0.75,
                        px: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        cursor: (disabled || isThisFacilityDisabled) ? 'default' : 'pointer',
                        bgcolor: isThisFacilityDisabled ? 'action.disabledBackground' : 'transparent',
                        opacity: isThisFacilityDisabled ? 0.6 : 1,
                        '&:hover': {
                          bgcolor: (disabled || isThisFacilityDisabled) ? 'action.disabledBackground' : 'action.hover'
                        }
                      }}
                      onClick={() => handleToggleFacility(facility.id)}
                    >
                      <Checkbox
                        checked={selectedFacilities.includes(facility.id)}
                        onChange={() => handleToggleFacility(facility.id)}
                        disabled={disabled || isThisFacilityDisabled}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {isThisFacilityDisabled ? (
                        <BlockIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                      ) : (
                        <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                      )}
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ color: isThisFacilityDisabled ? 'text.disabled' : 'text.primary' }}>
                          {facility.name}
                        </Typography>
                        {facility.location && (
                          <Typography variant="caption" color="text.secondary">
                            {facility.location}
                          </Typography>
                        )}
                      </Box>
                      {isThisFacilityDisabled && (
                        <Chip 
                          label="Assigned" 
                          size="small" 
                          color="warning" 
                          variant="outlined"
                          sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </Box>
                  );
                  
                  return isThisFacilityDisabled ? (
                    <Tooltip 
                      key={facility.id}
                      title={disabledReason} 
                      placement="left"
                      arrow
                    >
                      {facilityRow}
                    </Tooltip>
                  ) : facilityRow;
                })}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Empty State */}
        {groups.length === 0 && ungroupedFacilities.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No facilities available
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

FacilityAssignmentTree.propTypes = {
  selectedFacilities: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showSummary: PropTypes.bool,
  maxHeight: PropTypes.number,
  disabledFacilities: PropTypes.arrayOf(PropTypes.shape({
    facilityId: PropTypes.string.isRequired,
    reason: PropTypes.string
  }))
};

export default FacilityAssignmentTree;

